import { type ReactNode, useMemo } from 'react';

import { ConditionalChartProvider } from '../CustomDash/components/ConditionalChartProvider/ConditionalChartProvider';
import { EditModeProvider } from '../CustomDash/components/EditModeProvider/EditModeProvider';
import { FullPageErrorContext } from '../CustomDash/components/FullPageError/full-page-error.context';
import { PageProvider } from '../CustomDash/components/PageConfigProvider/PageConfigProvider';
import type { PositionedItem } from '../CustomDash/components/PageConfigProvider/types/item';
import { PageStateProvider } from '../CustomDash/components/PageState/PageStateProvider';
import { TableSelectionProvider } from '../CustomDash/components/TableSelectionProvider/TableSelectionProvider';
import { TextChartContentProvider } from '../CustomDash/components/TextChartContentProvider/TextChartContentProvider';
import { buildGlobalAssistantChartPreviewPage } from '../CustomDash/utils/generate-page-draft';
import type { EntityType } from '../CustomDash/utils/routing-helpers';

/**
 * Owner identity for synthetic preview pages (matches Page `Owner` shape).
 */
export type ChartRuntimeShellOwner = {
  id: string;
  username: string;
};

/**
 * Minimal CustomDash chart runtime: page state + page config + chart-level contexts.
 * URL hydration/sync for page state must stay off for chat-embedded previews.
 */
export type ChartRuntimeShellProps = {
  /** Stable React key segment (e.g. transcript message id). */
  runtimeKey: string;
  owner: ChartRuntimeShellOwner;
  entityType: EntityType;
  /** Seeds tab `items` so TabContentProvider breakdown / config matches the chart. */
  chartItem: PositionedItem;
  children: ReactNode;
};

export function ChartRuntimeShell({
  runtimeKey,
  owner,
  entityType,
  chartItem,
  children,
}: ChartRuntimeShellProps) {
  const chartErrorContextValue = useMemo(
    () => ({
      chartErrored: (_chartId: string) => {
        void _chartId;
      },
      resetChartError: (_chartId: string) => {
        void _chartId;
      },
    }),
    []
  );

  const previewPage = useMemo(
    () => buildGlobalAssistantChartPreviewPage(owner, entityType, chartItem),
    [owner, entityType, chartItem]
  );

  const pageKey = `${runtimeKey}:${previewPage.id}`;

  return (
    <EditModeProvider editMode={false}>
      <PageStateProvider
        key={pageKey}
        hydratePageStateFromUrl={false}
        syncPageStateToUrl={false}
        initialPageState={{
          metricOverrides: {},
          selections: {},
          primaryEntities: {},
          multiPrimaryEntities: {},
          dimensionSourceOverrides: {},
          initialSelections: {},
          activeTabId:
            previewPage.tabs?.find((tab) => tab.isActive)?.id ??
            previewPage.tabs?.[0]?.id ??
            '',
        }}
      >
        <PageProvider key={pageKey} initialConfig={previewPage}>
          {() => (
            <FullPageErrorContext.Provider value={chartErrorContextValue}>
              <TableSelectionProvider>
                <TextChartContentProvider>
                  <ConditionalChartProvider>
                    {children}
                  </ConditionalChartProvider>
                </TextChartContentProvider>
              </TableSelectionProvider>
            </FullPageErrorContext.Provider>
          )}
        </PageProvider>
      </PageStateProvider>
    </EditModeProvider>
  );
}
